import React, {ChangeEvent, Fragment, useEffect, useRef, useState} from 'react';
import {Navigate} from 'react-router-dom';

import {Box, Button, Skeleton, Stack, TextField, Typography, useTheme} from '@mui/material';
import {makeDeleteRequestFile} from 'main/factories/usecases/private/authorizations/request/delete-file';
import {makePostRequestUploadFile} from 'main/factories/usecases/private/authorizations/request/post-upload-file';
import {AlertInfo, AlertModal, CTAButtonFixed, StyledGrid} from 'presentation/components';
import {UploadFiles} from 'presentation/components/UploadFiles';
import {useUploadFile} from 'presentation/components/UploadFiles/hooks/useUploadFile';
import {AuthorizationsEnum, FeaturesEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';

import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {ModalError} from 'presentation/components/ModalError';
import {useExternalErrorMessageStore} from 'presentation/components/UploadFiles/store/useExternalErrorMessageStore';
import {useUploadedFilesStore} from 'presentation/components/UploadFiles/store/useUploadedFilesStore';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {DialogFinished} from 'presentation/pages/private/authorizations/components/DialogFinished';
import {useRequestAuthorization} from 'presentation/pages/private/authorizations/hooks';
import {useProviderSelection} from 'presentation/pages/private/authorizations/hooks/useProviderSelection';
import {useGetAuthDetail} from 'presentation/pages/private/authorizations/queries/useGetAuthDetail';
import {usePatchAuthRequestMutation} from 'presentation/pages/private/authorizations/queries/usePatchAuthRequestMutation';
import {usePatchAuthRequestStatusMutation} from 'presentation/pages/private/authorizations/queries/usePatchAuthRequestStatusMutation';
import {useAuthorizationsStore} from 'presentation/pages/private/authorizations/store/authorizationsStore';
import {PendingDialog} from 'presentation/pages/private/booking/components';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {CustomCTAButtonFixedAction, StyledCard} from './styles';
import {RequestInstructions} from '../RequestInstructions';
import {MAXIMUM_INFORMATION_LENGTH, validateRequestDetails} from './validation';

const {
	attachAlert,
	attachDescription,
	attachNote,
	requestingDoctorName,
	requestingDoctorPhone,
	requestingDoctorPhoneHint,
	preferredProvider,
	additionalInformation,
	additionalInformationHint,
	attachNoteHelperText,
	attachNotePlaceholder,
	sendRequest,
	sendingRequest,
	modalSendingAttach,
	attachTitle,
	missingInformationModal,
} = translations['pt-br'].privateFlow.requestAuthorizations.request.addAttachments;

const {description, headerTitle, title} =
	translations['pt-br'].privateFlow.requestAuthorizations.errorModal;

const {tryAgain, deleting, notice, agreementText} = translations['pt-br'].commons;

const {addFiles, error} = translations['pt-br'].others.uploadFiles;

type RequestAttachmentProps = {
	onBackToStart: () => void;
};

export function RequestAttachment({onBackToStart}: RequestAttachmentProps) {
	const {isEnabled: isAutomationFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_AUTOMACAO_AUTOSOL,
	);
	const {isMobile, isTablet} = useMediaQueries();
	const theme = useTheme();
	const nuControle = useAuthorizationsStore(state => state.nuControle);
	const providerObservation = useAuthorizationsStore(state => state.providerObservation);

	const [loadingState, setLoadingState] = useState(false);
	const [sendingAttachState, setSendingAttachState] = useState(false);
	const [protocolNumberState, setProtocolNumberState] = useState<string>();
	const [openConfirmationModalState, setOpenConfirmationModalState] = useState<boolean>(false);
	const [openPostSubmissionInstructionsState, setOpenPostSubmissionInstructionsState] =
		useState(false);
	const [openMissingInformationModalState, setOpenMissingInformationModalState] = useState(false);
	const [inputFieldState, setInputFieldState] = useState(providerObservation);
	const [inputFieldErrorState, setInputFieldErrorState] = useState(false);
	const informationInputRef = useRef<HTMLTextAreaElement>(null);

	const {companyPortal} = useAccountDataStore(state => state.accountData);

	const resetUploadedFilesState = useUploadedFilesStore(state => state.resetUploadedFiles);
	const resetExternalErrorMessage = useExternalErrorMessageStore(
		state => state.resetExternalErrorMessage,
	);
	const setExternalErrorMessage = useExternalErrorMessageStore(
		state => state.setExternalErrorMessage,
	);

	const {errorState, setErrorState, handleCloseModalError} = useRequestAuthorization();

	const {
		uploadedFiles,
		handleUpload,
		deleteFile,
		IS_UPLOADING,
		IS_DELETING,
		blockCloseModal,
		IS_DELETING_ERROR,
		resetDeleteError,
	} = useUploadFile({
		postFactory: makePostRequestUploadFile,
		deleteFactory: attachmentId => makeDeleteRequestFile({nuArquivo: attachmentId, nuControle}),
		nuControle,
	});
	const {IS_POST_AUTH_REQUEST_ERROR, IS_POST_AUTH_REQUEST_LOADING, handleCreateNewRequest} =
		useProviderSelection();

	const shouldOpenModalError = errorState || IS_DELETING_ERROR || IS_POST_AUTH_REQUEST_ERROR;
	const onCloseErrorModal =
		errorState || IS_POST_AUTH_REQUEST_ERROR ? () => setErrorState(false) : resetDeleteError;
	const modalErrorAction =
		errorState || IS_POST_AUTH_REQUEST_ERROR ? () => handleResend() : resetDeleteError;

	const inputFieldLength = inputFieldState?.length;

	const queryClient = useQueryClient();

	const {refetch: refetchAuthDetail} = useGetAuthDetail(Number(nuControle), false);

	const handleValidation = () => {
		setInputFieldErrorState(false);
		resetExternalErrorMessage();

		const validation = validateRequestDetails({
			attachments: uploadedFiles,
			information: inputFieldState,
		});

		if (validation.hasAttachmentError) {
			setExternalErrorMessage(error);
		} else {
			resetExternalErrorMessage();
		}

		if (validation.hasInformationError) {
			setInputFieldErrorState(true);
		}

		return validation;
	};

	const handleGetProtocolNumber = async () => {
		const {data} = await refetchAuthDetail();
		setProtocolNumberState(data?.nuProtocolo);
	};

	const {mutateAsync: patchAuthRequest} = usePatchAuthRequestMutation();
	const {mutateAsync: patchAuthRequestStatus} = usePatchAuthRequestStatusMutation();

	const handleGoToConfirmation = (continueWithoutInformation = false) => {
		if (IS_POST_AUTH_REQUEST_ERROR) {
			return;
		}

		if (IS_UPLOADING) {
			setSendingAttachState(true);
			return;
		}

		const validation = handleValidation();

		if (validation.hasAttachmentError || validation.hasInformationError || loadingState) {
			return;
		}

		if (validation.shouldConfirmEmptyInformation && !continueWithoutInformation) {
			setOpenMissingInformationModalState(true);
			return;
		}

		setOpenMissingInformationModalState(false);
		setOpenPostSubmissionInstructionsState(true);
	};

	const handleContinueToConclusion = async () => {
		setOpenPostSubmissionInstructionsState(false);
		setLoadingState(true);

		try {
			const trimmedInformation = inputFieldState.trim();
			if (trimmedInformation) {
				await patchAuthRequest({nuControle, observacoes: trimmedInformation});
			}

			await patchAuthRequestStatus({
				nuControle,
				nomeOperador: companyPortal?.operatorName,
				emailOperador: companyPortal?.operatorEmail,
				documentoOperador: companyPortal?.operatorDocument,
			});

			resetUploadedFilesState();
			await handleGetProtocolNumber();

			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AUTHORIZATIONS_REQUESTS]});
			setOpenConfirmationModalState(true);
		} catch (err) {
			setErrorState(true);
		} finally {
			setLoadingState(false);
		}
	};

	const handleResend = () => {
		handleCloseModalError(false);

		if (IS_POST_AUTH_REQUEST_ERROR) {
			handleCreateNewRequest();
			return;
		}

		handleGoToConfirmation();
	};

	const handleBackToFillInformation = () => {
		setOpenMissingInformationModalState(false);
		window.requestAnimationFrame(() => informationInputRef.current?.focus());
	};

	const handleClosePostSubmissionInstructions = () => {
		setOpenPostSubmissionInstructionsState(false);
	};

	useEffect(() => {
		if (!isAutomationFlagEnabled) {
			handleCreateNewRequest();
		}
	}, [isAutomationFlagEnabled]);

	if (isAutomationFlagEnabled && !nuControle) {
		return <Navigate to={AuthorizationsEnum.AUTHORIZATIONS_REQUESTS} replace={true} />;
	}

	return (
		<Fragment>
			<StyledGrid.Root spacing={isTablet ? 1 : 3} px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
				<StyledGrid.Item sm={12} md={12} lg={8}>
					<Stack gap={2} pb={isTablet ? 1 : 2} borderRadius={1}>
						<StyledCard>
							<Typography fontWeight={600} color="text.primary">
								{attachTitle}
							</Typography>
							<Typography color="text.primary">{attachDescription}</Typography>
							<AlertInfo.Root icon={false}>
								<AlertInfo.Description>{attachAlert}</AlertInfo.Description>
							</AlertInfo.Root>
							{IS_POST_AUTH_REQUEST_LOADING ? (
								<Skeleton
									variant="rectangular"
									width="100%"
									height={118}
									sx={{borderRadius: 1}}
								/>
							) : (
								<UploadFiles
									addFilesLabel={addFiles}
									errorDataTagId={
										AutoSolEnum.VISUALIZOU_ERRO_ADICIONAR_UM_ARQUIVO_AUTOSOL
									}
									dataTagId={AutoSolEnum.CLICK_ADICIONAR_ANEXO_AUTOSOL}
									onChangeFile={handleUpload}
									removeFile={deleteFile}
									values={uploadedFiles}
									isUploading={IS_UPLOADING}
								/>
							)}
						</StyledCard>

						{IS_POST_AUTH_REQUEST_LOADING ? (
							<StyledCard>
								<Skeleton
									variant="rectangular"
									width="100%"
									height={118}
									sx={{borderRadius: 1}}
								/>
							</StyledCard>
						) : (
							<StyledCard>
								<Typography color="text.primary" variant="body1" fontWeight={600}>
									{attachNote}
								</Typography>
								<Box
									component="ul"
									color="text.primary"
									sx={{m: 0, pl: 3, listStyle: 'disc'}}>
									<Box component="li">
										<Typography variant="body1">
											{requestingDoctorName}
										</Typography>
									</Box>
									<Box component="li">
										<Typography variant="body1">
											{requestingDoctorPhone}
											<Typography
												component="span"
												display="block"
												variant="caption"
												fontStyle="italic">
												{requestingDoctorPhoneHint}
											</Typography>
										</Typography>
									</Box>
									<Box component="li">
										<Typography variant="body1">{preferredProvider}</Typography>
									</Box>
									<Box component="li">
										<Typography variant="body1">
											{additionalInformation}{' '}
											<Typography
												component="span"
												variant="caption"
												fontStyle="italic">
												{additionalInformationHint}
											</Typography>
										</Typography>
									</Box>
								</Box>

								<TextField
									inputRef={informationInputRef}
									placeholder={attachNotePlaceholder}
									slotProps={{
										inputLabel: {
											sx: {
												whiteSpace: 'break-spaces',
											},
										},
										formHelperText: {
											sx: {
												margin: 0,
											},
										},
									}}
									multiline
									rows={4}
									value={inputFieldState}
									onChange={(event: ChangeEvent<HTMLInputElement>) => {
										const {value} = event.target;
										setInputFieldErrorState(false);
										if (value.length <= MAXIMUM_INFORMATION_LENGTH) {
											setInputFieldState(value);
										}
									}}
									error={inputFieldErrorState}
									helperText={
										<Box
											sx={{display: 'flex', justifyContent: 'space-between'}}>
											{attachNoteHelperText}
											<Typography
												variant="caption"
												textAlign="end"
												sx={{
													display: 'inline-block',
													pl: '12px',
													marginTop: 'auto',
												}}>
												{inputFieldLength}/{MAXIMUM_INFORMATION_LENGTH}
											</Typography>
										</Box>
									}
									sx={{
										'& .MuiInputBase-input::placeholder': {
											color: inputFieldErrorState
												? theme.palette.error.main
												: 'text.secondary',
											opacity: 1,
										},
									}}
								/>
							</StyledCard>
						)}
					</Stack>
					<CTAButtonFixed.Root $isTablet={isTablet}>
						<CustomCTAButtonFixedAction
							fullWidth={isTablet}
							onClick={() => handleGoToConfirmation()}
							size="large"
							sx={{alignSelf: 'flex-end'}}
							data-tag-id={AutoSolEnum.AS_CLICK_ENVIAR_SOLICITACAO}
							disabled={IS_POST_AUTH_REQUEST_LOADING || loadingState}>
							{loadingState ? sendingRequest : sendRequest}
						</CustomCTAButtonFixedAction>
					</CTAButtonFixed.Root>
				</StyledGrid.Item>
				<StyledGrid.Item sm={12} md={12} lg={4}>
					<RequestInstructions
						useDrawer={isTablet}
						isOpen={openPostSubmissionInstructionsState || undefined}
						onClose={
							openPostSubmissionInstructionsState
								? handleClosePostSubmissionInstructions
								: undefined
						}
						onBackToStart={onBackToStart}
						onContinue={
							openPostSubmissionInstructionsState
								? handleContinueToConclusion
								: undefined
						}
					/>
				</StyledGrid.Item>
			</StyledGrid.Root>

			{IS_DELETING ? (
				<PendingDialog open={true} onClose={blockCloseModal} text={deleting} />
			) : null}

			<AlertModal
				onClose={() => setSendingAttachState(false)}
				open={sendingAttachState}
				headerTitle={notice}
				title={modalSendingAttach.title}
				description={modalSendingAttach.description}
				variant="info"
				buttonLabel={agreementText}
				customIconName="timelapse-filled"
			/>

			<AlertModal
				onClose={() => setOpenMissingInformationModalState(false)}
				open={openMissingInformationModalState}
				headerTitle={missingInformationModal.headerTitle}
				title={missingInformationModal.title}
				description={missingInformationModal.description}
				variant="info"
				customMaxWidth={420}
				footerContent={
					<Stack width="100%" gap={1}>
						<Button
							variant="contained"
							size="large"
							fullWidth
							onClick={handleBackToFillInformation}>
							{missingInformationModal.backAndFill}
						</Button>
						<Button
							variant="outlined"
							size="large"
							fullWidth
							onClick={() => handleGoToConfirmation(true)}>
							{missingInformationModal.continueWithoutFilling}
						</Button>
					</Stack>
				}
			/>

			<ModalError
				open={shouldOpenModalError}
				onClose={onCloseErrorModal}
				headerTitle={headerTitle}
				title={title}
				description={description}
				maxWidth={420}
				firstButton={{
					title: tryAgain,
					handleClick: modalErrorAction,
				}}
			/>

			{openConfirmationModalState ? (
				<DialogFinished nuProtocolo={protocolNumberState} />
			) : null}
		</Fragment>
	);
}
